import FavoriteIcon from '@mui/icons-material/Favorite';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import WatchIcon from '@mui/icons-material/Watch';
import { Box, Grid, ImageList, ImageListItem, ImageListItemBar, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, Theme } from '@mui/system';
import moment from 'moment';
import { useState } from 'react';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import { mediaTypeIcon, secondsToMinutesAndSecondsString } from '../../../../utils/common';
import AudioPlayerDialog from '../../../common/AudioPlayerDialog';

type Props = {
    thumbnails: any[];
    medias: OnlyFansVaultMedias.RestructuredResponseItem[];
    mediasPreviews: number[];
    setMediasPreviews: React.Dispatch<React.SetStateAction<number[]>>;
    getAudio: (url: string) => Promise<Blob | null>;
    theme: Theme;
    disabled?: boolean;
};

const MessageUsersPriceLockPreviewSelector = (props: Props) => {
    const { thumbnails, medias, mediasPreviews, setMediasPreviews, getAudio, theme, disabled } = props;
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    const [playerOpen, setPlayerOpen] = useState(false);
    const [selectedAudioUrl, setSelectedAudioUrl] = useState('');

    const handleMediaClicked = (mediaId: number) => {
        if (mediasPreviews.includes(mediaId)) {
            setMediasPreviews((prevMedias: number[]) => {
                return prevMedias.filter(m => m !== mediaId);
            });
        } else {
            setMediasPreviews((prevMedias: number[]) => {
                return [...prevMedias, mediaId];
            });
        }
    };

    return (
        <>
            <ImageList cols={isLargeScreen ? 5 : 3} rowHeight={136}>
                {medias.map(media => {
                    const previewIndex = mediasPreviews.indexOf(media.id);

                    return (
                        <ImageListItem
                            key={media.id}
                            sx={{
                                cursor: disabled ? 'default' : 'pointer',
                                borderStyle: 'solid',
                                borderWidth: '2px',
                                borderColor: medias.find(m => m.id === media.id)
                                    ? theme.palette.secondary.main
                                    : theme.palette.common.white,
                                ':hover': disabled
                                    ? {}
                                    : {
                                          borderStyle: 'solid',
                                          borderWidth: '2px',
                                          borderColor: theme.palette.primary.main,
                                      },
                                ...(disabled && {
                                    opacity: 0.5,
                                }),
                            }}
                        >
                            {media.type !== 'audio' ? (
                                media.cachedThumb ? (
                                    <img
                                        src={media.cachedThumb}
                                        alt={media.id.toString()}
                                        loading="lazy"
                                        height={150}
                                        width={150}
                                        onClick={() => {
                                            if (!disabled) {
                                                handleMediaClicked(media.id);
                                            }
                                        }}
                                    />
                                ) : thumbnails.find(thumbnail => thumbnail.thumbId === media.id && thumbnail.blob) ? (
                                    <img
                                        src={URL.createObjectURL(
                                            thumbnails.find(thumbnail => thumbnail.thumbId === media.id && thumbnail.blob).blob,
                                        )}
                                        alt={media.id.toString()}
                                        loading="lazy"
                                        height={150}
                                        width={150}
                                        onClick={() => {
                                            if (!disabled) {
                                                handleMediaClicked(media.id);
                                            }
                                        }}
                                    />
                                ) : (
                                    <Skeleton />
                                )
                            ) : (
                                <Grid
                                    container
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{
                                        backgroundColor: theme.palette.grey[300],
                                        height: '100%',
                                    }}
                                    onClick={() => {
                                        if (!disabled) {
                                            handleMediaClicked(media.id);
                                        }
                                    }}
                                >
                                    <Grid item xs={12} sx={{ paddingBottom: theme.spacing(5) }}>
                                        <Grid container justifyContent="center" alignItems="center">
                                            <Grid item xs={12}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <PlayCircleIcon
                                                        onClick={e => {
                                                            e.stopPropagation();

                                                            if (media.src && !disabled) {
                                                                setSelectedAudioUrl(media.src);
                                                                setPlayerOpen(true);
                                                            }
                                                        }}
                                                        fontSize="large"
                                                        sx={{
                                                            ':hover': {
                                                                color: theme.palette.primary.main,
                                                            },
                                                            height: theme.spacing(4),
                                                        }}
                                                    />
                                                </div>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                                                        {secondsToMinutesAndSecondsString(media.duration || 0)}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <ImageListItemBar
                                title={moment(media.createdAt).calendar({
                                    sameDay: '[Today]',
                                    nextDay: '[Tomorrow]',
                                    nextWeek: 'MMM Mo',
                                    lastDay: '[Yesterday]',
                                    lastWeek: 'MMM Mo',
                                    sameElse: 'MMM Mo',
                                })}
                                subtitle={
                                    <Stack direction="column" spacing={0.5}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <FavoriteIcon sx={{ fontSize: '8pt' }} />
                                            <div>{media.counters.likesCount.toLocaleString()} Likes</div>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <WatchIcon sx={{ fontSize: '8pt' }} />
                                            <div>{media.duration ? secondsToMinutesAndSecondsString(media.duration) : '-'}</div>
                                        </Stack>
                                    </Stack>
                                }
                                actionIcon={mediaTypeIcon(media.type, theme)}
                                onClick={() => {
                                    if (!disabled) {
                                        handleMediaClicked(media.id);
                                    }
                                }}
                                sx={{
                                    backgroundColor: mediasPreviews.includes(media.id)
                                        ? alpha(theme.palette.secondary.main, 0.8)
                                        : '#00000080',
                                    // Make the font size smaller
                                    '.MuiImageListItemBar-title': {
                                        fontSize: '0.8rem',
                                    },
                                    '.MuiImageListItemBar-actionIcon': {
                                        marginTop: theme.spacing(0.5),
                                        marginRight: theme.spacing(0.5),
                                        // Make the icon smaller
                                        '& svg': {
                                            height: '1.2rem',
                                            width: '1.2rem',
                                        },
                                    },
                                    // Title Wrap
                                    '& .MuiImageListItemBar-titleWrap': {
                                        paddingTop: theme.spacing(0.5),
                                        paddingBottom: theme.spacing(0.5),
                                        paddingLeft: theme.spacing(0.5),
                                        paddingRight: theme.spacing(0.5),
                                    },
                                }}
                            />
                            {previewIndex !== -1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: theme.spacing(0.5),
                                        left: theme.spacing(0.5),
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                                        color: theme.palette.secondary.contrastText,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                    }}
                                >
                                    {previewIndex + 1}
                                </Box>
                            )}
                        </ImageListItem>
                    );
                })}
            </ImageList>
            <AudioPlayerDialog open={playerOpen} setOpen={setPlayerOpen} url={selectedAudioUrl} getAudio={getAudio} />
        </>
    );
};

export default MessageUsersPriceLockPreviewSelector;
